import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Card,
  CardBody,
  Button as ReactButton,
  Modal as Mod,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Tooltip,
} from 'reactstrap';
import PulseLoader from 'react-spinners/PulseLoader';
import isEmpty from 'lodash/isEmpty';
import { map } from 'lodash';
import { UnfreezeUI } from './../../assets/plugins/nucleo/js/freeze-ui';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { memberActionTypes } from '@store/Member/Member.actionTypes';
import { Button, buttonVariants } from '@components/Button';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import { useUserTeamsForCompany } from '../../hooks/useUserTeamsForCompany';
import { useSavedActiveTeamLabel } from '../../hooks/useSavedActiveTeamLabel';
import teamDatabase from '../../services/team';
import { db } from '../../config/firebase.config';
import { fetchEmployeeExtendedDetails } from '../../services/employeeMemberEnrichment';
import { queryKeys } from '../../config/queryKeys';
import { FiCopy } from 'react-icons/fi';
import { Modal } from '@/components/Modal';
import { ManageCardsModal } from '@/modals/ManagePaymentCards/ManageCardsModal.component';
import styles from './Profile.module.scss';
import { profileTabs } from './Profile.constants';
import { ProfileTab } from './ProfileTab.component';
import { PlanBillingTab } from './PlanBillingTab.component';
import { TeamSettingsTab } from './TeamSettingsTab.component';
import { ProductivityAppsTab } from './ProductivityAppsTab.component';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import classNames from 'classnames';

export const ProfileComponent = () => {
  const [activeTab, setActiveTab] = useState(profileTabs[0].id);
  const [filteredTabs, setFilteredTabs] = useState(profileTabs);
  const [isManageCardsModalOpen, setIsManageCardsModalOpen] = useState(false);
  const user = useSelector((state) => ({
    userProfile: state.auth?.profile,
    activeCompany: state.auth?.profile?.activeCompany,
    companyList: state.auth?.profile?.companyList,
    activeTeamId: state.team.selectedTeamID,
    activeUserId: state.team.activeUserID,
    all: state.team.all,
    role: state.auth?.profile?.activeCompany?.role,
  }));
  const isAdmin = user?.role === 'Admin';
  useEffect(() => {
    setFilteredTabs(
      profileTabs.filter((tab) => (tab.adminOnly ? isAdmin : true))
    );
  }, [isAdmin]);
  const team = useSelector((state) => ({
    loading: state.team.loading,
    activeTeam: state.team.activeTeam,
    activeUserID: state.team.activeUserID,
    teams: state.team.teams,
    profileID: state.auth.profile.id,
    all: state.team.all,
    selectedTeamID: state.team.selectedTeamID,
    active: state.team.active,
    week: state.team.week,
  }));
  const dispatch = useDispatch();
  const companyId = user?.activeCompany?.companyId;
  const userUID = user?.userProfile?.id;
  const {
    teams: companyTeams,
    loading: activeTeamsLoading,
    error: activeTeamsError,
  } = useUserTeamsForCompany(userUID, companyId);
  const { savedActiveTeamLabel, savedActiveTeamLoading } =
    useSavedActiveTeamLabel(userUID);

  const {
    data: extendedDetails,
    isLoading: loadingLicenseGuid,
    isError: licenseGuidError,
  } = useQuery({
    queryKey: queryKeys.employeeList.extendedDetails(userUID),
    queryFn: async () => {
      const data = await fetchEmployeeExtendedDetails(db, userUID);
      if (!data) {
        throw new Error('Member not found');
      }
      return data;
    },
    enabled: Boolean(userUID),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const licenseGuidDisplay =
    licenseGuidError || !extendedDetails
      ? '—'
      : extendedDetails.guid === 'N/A' || !extendedDetails.guid
        ? 'N/A'
        : extendedDetails.guid;

  const licenseGuidCopyable =
    licenseGuidDisplay &&
    licenseGuidDisplay !== '—' &&
    licenseGuidDisplay !== 'N/A';

  const selectedTeamName = useMemo(() => {
    if (!companyId) {
      return '—';
    }
    const a = team?.active;
    if (a && typeof a.Team === 'string' && a.Team.trim()) {
      return a.Team;
    }
    const id = team?.selectedTeamID;
    if (!id || !team?.teams?.data?.length) {
      return '—';
    }
    const row = team.teams.data.find((r) => {
      if (r?.teamRemoved) return false;
      const rid =
        r?.activeTeamid ?? r?.teamId ?? (typeof r?.id === 'string' ? r.id : '');
      return rid === id;
    });
    return row?.Team ?? '—';
  }, [companyId, team?.active, team?.selectedTeamID, team?.teams?.data]);

  const [modal, setModal] = useState(false);
  const [activeTeamModal, setActiveTeamModal] = useState(false);
  const [rSelected, setRSelected] = useState(0);
  const [toolOpenChangeTeam, setToolOpenChangeTeam] = useState(false);
  const [toolOpenActiveTeam, setToolOpenActiveTeam] = useState(false);

  const toggle = () => {
    setModal(!modal);
  };

  const toggleActiveTeamModal = () => {
    setActiveTeamModal(!activeTeamModal);
  };

  const copyLicenseGuid = async () => {
    const id = licenseGuidCopyable ? String(licenseGuidDisplay) : '';
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      addNotification({
        message: 'GUID copied to clipboard',
        level: notificationStates.success,
      });
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = id;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        addNotification({
          message: 'GUID copied to clipboard',
          level: notificationStates.success,
        });
      } catch {
        addNotification({
          message: 'Could not copy — select the text and copy manually.',
          level: notificationStates.warning,
        });
      }
    }
  };

  /** Session / UI team (Redux) — “Change Team”. Does not write membersDatabase active fields. */
  const handleChangeTeam = (value) => {
    let obj = { Team: value.Team, activeTeamid: value.activeTeamid };
    localStorage.setItem('teamData', JSON.stringify(obj));
    dispatch({
      type: teamActionTypes.SET_SELECT_TEAM_ID,
      payload: {
        selectedTeamID: value.activeTeamid,
        activeUserID: user.userProfile?.id,
        activeTeam: {
          Team: value.Team,
          activeTeamid: value.activeTeamid,
        },
        companyId: user?.activeCompany?.companyId,
      },
    });
    setModal(false);
    setTimeLogDays(rSelected, value.activeTeamid);
    UnfreezeUI();
  };

  /** Saved active team on membersDatabase — “Change Active Team”. Firestore client only; does not change Redux session. */
  const handleChangeActiveTeam = async (value) => {
    const teamDocId = value?.activeTeamid ?? value?.teamId;
    const teamLabel = value?.Team ?? value?.teamName ?? '';
    if (!teamDocId) return;
    const cid = user?.activeCompany?.companyId;
    const uid = user?.userProfile?.id;
    if (!cid || !uid) return;

    setActiveTeamModal(false);

    const updateRes = await teamDatabase.updateMemberActiveCompanyAndTeam(
      uid,
      cid,
      teamDocId
    );
    if (updateRes?.success) {
      addNotification({
        message: `Active team saved: ${teamLabel}`,
        level: notificationStates.success,
      });
      const emailRes = await teamDatabase.sendActiveTeamChangeEmail({
        companyName: user?.activeCompany?.companyName || '',
        teamName: teamLabel,
      });
      if (!emailRes?.success) {
        addNotification({
          message:
            'Active team saved, but the confirmation email could not be sent.',
          level: notificationStates.warning,
        });
      }
    } else {
      addNotification({
        message:
          updateRes?.error?.message ||
          'Could not save active team. Check permissions and try again.',
        level: notificationStates.error,
      });
    }
  };

  const setTimeLogDays = (days, id, start, end) => {
    dispatch({
      type: teamActionTypes.SET_TIMELOG_DAY_RANGE,
      payload: { days, start, end },
    });

    dispatch({
      type: memberActionTypes.SET_TIMELOG_DAY_RANGE,
      payload: { days, start, end },
    });

    dispatch({
      type: teamActionTypes.SET_TODAY_TIMES,
      payload: {
        selectedTeamID: id,
        day: days,
      },
    });
    dispatch({
      type: teamActionTypes.SET_TRENDS_DATA,
      payload: {
        selectedTeamID: id,
        week: team.week,
      },
    });

    if (user.all) {
      dispatch({
        type: teamActionTypes.GET_ALL_TEAM_TIMELOG,
        payload: {
          days,
          start,
          end,
          teamId: id,
        },
      });
    } else {
      dispatch({
        type: teamActionTypes.GET_MANAGER_TEAM_COLLECTION,
        payload: {
          days,
          start,
          end,
          activeTeamId: id,
          activeUserId: user.activeUserId,
        },
      });
    }
  };

  return (
    <>
      <div className={styles.profilePage}>
        <div className={styles.profileSidebar}>
          {filteredTabs.map((tab) => {
            return (
              <div
                key={tab.id}
                className={classNames(styles.profileTabButton, {
                  [styles.profileTabButton_active]: activeTab === tab.id,
                })}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                <Text
                  variant={textVariantsEnum.Text_md}
                  fontWeight={fontWeightEnum.Normal}
                >
                  {tab.name}
                </Text>
              </div>
            );
          })}
        </div>
        <div className={styles.profileContent}>
          {/* <ProfileTab /> */}
          {activeTab === 'profilePage' && <ProfileTab />}
          {activeTab === 'planAndBilling' && <PlanBillingTab />}
          {activeTab === 'teamSettings' && <TeamSettingsTab />}
          {activeTab === 'productivityApps' && <ProductivityAppsTab />}
        </div>
      </div>
      {/* <div style={{ paddingTop: '5rem!important', position: 'relative' }}>
        {isAdmin && (
          <>
            {' '}
            <div className={styles.container}>
              <button
                className={styles.button}
                onClick={() => setIsManageCardsModalOpen(true)}
              >
                <span className={styles.icon}>
                  <i className="ni ni-credit-card"></i>
                </span>
                Manage Cards
              </button>
            </div>
            <Modal
              isVisible={isManageCardsModalOpen}
              onClose={() => setIsManageCardsModalOpen(false)}
            >
              <ManageCardsModal />
            </Modal>{' '}
          </>
        )}
        <Container className="pt-md-2" fluid>
          <Card>
            <CardBody className="d-flex justify-content-center flex-column align-items-center">
              <h3 style={{ color: '#B2DD4C' }}>Profile</h3>
              <h4 className="mt-3">
                Name:{' '}
                <span style={{ color: '#B2DD4C' }}>
                  {user?.userProfile?.name}
                </span>
              </h4>
              <div
                className="mt-3 text-center px-2"
                style={{ maxWidth: 'min(100%, 520px)' }}
              >
                <div className="text-muted small mb-1">GUID</div>
                <div className="d-flex flex-wrap align-items-center justify-content-center gap-2">
                  <code
                    className="text-left"
                    style={{
                      color: '#B2DD4C',
                      fontSize: '0.85rem',
                      wordBreak: 'break-all',
                      userSelect: 'all',
                      cursor: 'text',
                      background: 'rgba(0,0,0,0.04)',
                      padding: '6px 10px',
                      borderRadius: 6,
                      flex: '1 1 220px',
                    }}
                    title={
                      licenseGuidCopyable
                        ? String(licenseGuidDisplay)
                        : undefined
                    }
                  >
                    {loadingLicenseGuid ? (
                      <PulseLoader color="#B2DD4C" size={6} />
                    ) : (
                      licenseGuidDisplay
                    )}
                  </code>
                  {licenseGuidCopyable ? (
                    <button
                      type="button"
                      className="btn btn-link p-1 m-0 align-self-center border-0 shadow-none"
                      style={{ color: '#B2DD4C', lineHeight: 1 }}
                      onClick={copyLicenseGuid}
                      aria-label="Copy GUID"
                      title="Copy"
                    >
                      <FiCopy size={22} />
                    </button>
                  ) : null}
                </div>
              </div>
              <h4 className="mt-3">
                Active Company:{' '}
                <span style={{ color: '#B2DD4C' }}>
                  {user?.activeCompany?.companyName}
                </span>
              </h4>
              <h4 className="mt-3">
                Selected Team:{' '}
                <span style={{ color: '#B2DD4C' }}>{selectedTeamName}</span>
              </h4>
              <h4 className="mt-3">
                Active Team:{' '}
                <span style={{ color: '#B2DD4C' }}>
                  {savedActiveTeamLoading ? (
                    <PulseLoader color="#B2DD4C" size={6} />
                  ) : (
                    (savedActiveTeamLabel ?? '—')
                  )}
                </span>
              </h4>

              <div
                className="d-flex flex-column align-items-center mt-3 w-100"
                style={{ maxWidth: 360 }}
              >
                {companyId && !isEmpty(team.teams) ? (
                  team?.teams?.data?.length <= 1 ? (
                    <>
                      <Tooltip
                        placement="right"
                        isOpen={toolOpenChangeTeam}
                        target="profileChangeTeamOne"
                        toggle={() =>
                          setToolOpenChangeTeam(!toolOpenChangeTeam)
                        }
                      >
                        Only one team detected. It is selected as default.
                      </Tooltip>
                      <Button
                        id="profileChangeTeamOne"
                        text="Change Team"
                        variant={buttonVariants.primary}
                        onClick={() => toggle()}
                      />
                    </>
                  ) : (
                    <Button
                      id="profileChangeTeam"
                      text="Change Team"
                      variant={buttonVariants.primary}
                      onClick={() => toggle()}
                    />
                  )
                ) : null}

                {!companyId ? (
                  <p className="text-muted small text-center mt-3 mb-0">
                    Select a company in the header to set your saved active
                    team.
                  </p>
                ) : activeTeamsLoading ? (
                  <div className="mt-3">
                    <PulseLoader color="#4f58bb" size={8} />
                  </div>
                ) : activeTeamsError ? (
                  <p className="text-danger small text-center mt-3 mb-0">
                    {activeTeamsError}
                  </p>
                ) : companyTeams?.length === 0 ? (
                  <p className="text-muted small text-center mt-3 mb-0">
                    No teams for this company to set as active.
                  </p>
                ) : companyTeams?.length === 1 ? (
                  <>
                    <Tooltip
                      placement="right"
                      isOpen={toolOpenActiveTeam}
                      target="profileActiveTeamOne"
                      toggle={() => setToolOpenActiveTeam(!toolOpenActiveTeam)}
                    >
                      Only one team in this company for your account.
                    </Tooltip>
                    <Button
                      id="profileActiveTeamOne"
                      text="Change Active Team"
                      className="mt-2"
                      variant={buttonVariants.primary}
                      onClick={() => toggleActiveTeamModal()}
                    />
                  </>
                ) : (
                  <Button
                    id="profileChangeActiveTeam"
                    text="Change Active Team"
                    className="mt-2"
                    variant={buttonVariants.primary}
                    onClick={() => toggleActiveTeamModal()}
                  />
                )}
              </div>
            </CardBody>
          </Card>
        </Container>
      </div> */}

      <Mod isOpen={modal} toggle={toggle} style={{ marginTop: '100px' }}>
        <ModalHeader toggle={toggle}>Choose a Team</ModalHeader>
        <ModalBody>
          <Container>
            <Row className="d-flex justify-content-center align-items-center">
              {!isEmpty(team.teams) ? (
                map(team.teams.data, (value, index) => {
                  if (value.teamRemoved) return null;
                  return (
                    <Col
                      md={12}
                      className="mt-1"
                      key={value?.activeTeamid || index}
                    >
                      <ReactButton
                        className="modal-button w-100"
                        onClick={() => handleChangeTeam(value)}
                      >
                        {value.Team}
                      </ReactButton>
                    </Col>
                  );
                })
              ) : (
                <PulseLoader color="#4f58bb" />
              )}
            </Row>
          </Container>
        </ModalBody>
      </Mod>

      <Mod
        isOpen={activeTeamModal}
        toggle={toggleActiveTeamModal}
        style={{ marginTop: '100px' }}
      >
        <ModalHeader toggle={toggleActiveTeamModal}>
          Choose active team (saved on your account)
        </ModalHeader>
        <ModalBody>
          <Container>
            <Row className="d-flex justify-content-center align-items-center">
              {activeTeamsLoading ? (
                <Col md={12} className="text-center py-3">
                  <PulseLoader color="#4f58bb" />
                </Col>
              ) : activeTeamsError ? (
                <Col md={12} className="text-center text-danger py-2">
                  {activeTeamsError}
                </Col>
              ) : companyTeams?.length ? (
                companyTeams.map((t) => (
                  <Col md={12} className="mt-1" key={t.teamId}>
                    <ReactButton
                      className="modal-button w-100"
                      onClick={() =>
                        handleChangeActiveTeam({
                          Team: t.teamName,
                          activeTeamid: t.teamId,
                          teamId: t.teamId,
                          teamName: t.teamName,
                        })
                      }
                    >
                      {t.teamName}
                    </ReactButton>
                  </Col>
                ))
              ) : (
                <Col md={12} className="text-center text-muted py-2">
                  No teams found for this company.
                </Col>
              )}
            </Row>
          </Container>
        </ModalBody>
      </Mod>
    </>
  );
};
